import { useMemo } from 'react'

// CateringImpactSection — drops into the catering inquiry detail
// surface for status='confirmed' inquiries. DETERMINISTIC view of what
// a confirmed booking implies for the kitchen: staff headcount,
// inventory line items, and event timing.
//
// READ-ONLY. NO AI calls live here. NO write path to
// schedule_assignments. The Skeptic will reject any insert/update/delete
// inside this file — that's a follow-up ship's concern.
//
// Reads from inquiry.quote_payload directly (the same structured
// response generate-catering-quote returned, persisted by 0050).

const STAFF_RATIO = 11 // 1 cook per ~11 guests, conservative.

function formatEventDate(isoDate, eventTime) {
  if (!isoDate) return ''
  // YYYY-MM-DD parsed as UTC to avoid the local-tz off-by-one.
  const d = new Date(`${isoDate}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return isoDate
  const dow = d.toLocaleString('en-US', { weekday: 'short', timeZone: 'UTC' })
  const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })
  const day = d.getUTCDate()
  const base = `${dow} ${month} ${day}`
  if (eventTime) {
    // event_time stored as HH:MM[:SS]; show the HH:MM only.
    return `${base} · ${String(eventTime).slice(0, 5)}`
  }
  return base
}

export default function CateringImpactSection({ inquiry, onJumpToSchedule }) {
  const quote = inquiry?.quote_payload || null
  const headcount = Number(inquiry?.headcount || 0)

  const staffNeeded = useMemo(() => {
    if (!headcount || headcount <= 0) return null
    return Math.ceil(headcount / STAFF_RATIO)
  }, [headcount])

  const lineItems = useMemo(() => {
    if (!quote || !Array.isArray(quote.line_items)) return []
    return quote.line_items.slice(0, 10)
  }, [quote])

  // Placeholder when no quote payload exists (confirmed before 0050
  // shipped, or manually flipped to confirmed without going through
  // the AI quote flow).
  if (!quote) {
    return (
      <section className="mt-4 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
        <div className="text-[11px] font-medium text-black/50">
          Booking impact
        </div>
        <h3 className="mt-0.5 text-sm font-semibold text-black/85">
          No saved quote — impact view unavailable
        </h3>
        <p className="mt-2 text-xs text-black/55">
          Generate a quote and apply it to this inquiry to see the staff + prep impact here.
        </p>
      </section>
    )
  }

  return (
    <section className="mt-4 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
      <div className="text-[11px] font-medium text-black/50">
        Booking impact
      </div>
      <h3 className="mt-0.5 text-sm font-semibold text-black/85">
        What this confirmed booking implies
      </h3>

      {/* Event date */}
      {inquiry?.event_date && (
        <div className="mt-3 rounded-xl bg-emerald-50/60 p-3">
          <div className="text-[11px] font-medium uppercase tracking-wide text-emerald-900/70">
            Event
          </div>
          <div className="mt-0.5 text-sm font-medium text-emerald-900">
            {formatEventDate(inquiry.event_date, inquiry.event_time)}
          </div>
          {typeof onJumpToSchedule === 'function' && (
            <button
              type="button"
              onClick={() => onJumpToSchedule(inquiry.event_date)}
              className="mt-2 rounded-md bg-white px-2.5 py-1 text-xs font-medium text-emerald-800 shadow-sm hover:bg-emerald-50"
            >
              View this week on Schedule →
            </button>
          )}
        </div>
      )}

      {/* Staff impact */}
      {staffNeeded !== null && (
        <div className="mt-3 rounded-xl border border-black/10 bg-white p-3">
          <div className="text-[11px] font-medium uppercase tracking-wide text-black/50">
            Staff impact
          </div>
          <div className="mt-1 text-sm text-black/85">
            Estimated kitchen staff: <span className="font-semibold tabular-nums">{staffNeeded}</span>
          </div>
          <div className="mt-0.5 text-xs text-black/55">
            1 cook per ~{STAFF_RATIO} guests · {headcount} guests booked
          </div>
        </div>
      )}

      {/* Inventory delta */}
      {lineItems.length > 0 && (
        <div className="mt-3 rounded-xl border border-black/10 bg-white p-3">
          <div className="text-[11px] font-medium uppercase tracking-wide text-black/50">
            Items to prep
          </div>
          <div className="mt-0.5 text-[11px] text-black/45">
            From your saved quote
          </div>
          <ul className="mt-2 space-y-1.5">
            {lineItems.map((li, idx) => (
              <li
                key={`${li?.name || 'item'}-${idx}`}
                className="flex items-start justify-between gap-3 border-b border-black/5 pb-1.5 last:border-b-0 last:pb-0"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-black/85">
                  {li?.name || 'Item'}
                </span>
                {li?.quantity && (
                  <span className="text-xs tabular-nums text-black/65">
                    {li.quantity}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
